import { ATTRIBUTE_NAMES, DAMAGE_TYPES, applyResistances, createDamage, mapDamageTypes, } from './stats.js';
/**
 * The combat layer: one swing, resolved.
 *
 * Every formula here is D2's, taken from the mechanics write-ups rather than
 * invented, and every one is cited where it is implemented. The layer is pure —
 * it reads a `CombatantStats` pair, draws from the injected `Rng` port, and
 * returns a value. It owns no state, no clock and no items.
 *
 * ## The roll budget of a swing
 *
 * Rolls are drawn in a fixed order and only while they can still change the
 * outcome, so a replay consumes the identical slice of the stream:
 *
 *   1. hit    — always
 *   2. block  — only once the attack has landed
 *   3. crit   — only once the block has failed
 *   4. damage — one roll per type, in `DAMAGE_TYPES` order, always all five
 *
 * A miss costs 1 draw, a block 2, a landed hit 8. Rolling all five damage types
 * even when a weapon deals none of a type keeps that budget a constant.
 */
/** D2 clamps the chance to hit to this floor: no attacker is ever hopeless. */
export const MIN_HIT_CHANCE = 5;
/** …and to this ceiling: no attacker ever stops missing entirely. */
export const MAX_HIT_CHANCE = 95;
/** D2's blocking cap, identical for every class. */
export const MAX_BLOCK_CHANCE = 75;
/** The dexterity the block formula gives away before blocking scales at all. */
export const BLOCK_DEXTERITY_OFFSET = 15;
/** Critical Strike and Deadly Strike both double physical damage. */
export const CRITICAL_DAMAGE_MULTIPLIER = 2;
/** D2's animation clock: a static 256, chosen by Blizzard to keep rounding error out of the frame maths. */
export const ANIMATION_SPEED = 256;
/** An unmodified animation runs at 100% rate. */
export const BASE_ANIMATION_RATE = 100;
/** The constant in the item-IAS diminishing-returns curve. */
export const IAS_DIMINISHING_CONSTANT = 120;
/** The engine cannot render a swing shorter than a single frame. */
export const MIN_FRAMES_PER_ATTACK = 1;
const PERCENT = 100;
const clamp = (value, low, high) => Math.min(Math.max(value, low), high);
/** Draws one roll and reports whether it came in under a percentage chance. */
const rollPercent = (rng, chance) => rng.next() * PERCENT < chance;
/**
 * Chance for `attacker` to land a blow on `defender`, in percent.
 *
 * D2's attack rating formula, clamped:
 *
 *   ToHit = min(max(200 * (AR / (AR + DEF)) * (alvl / (alvl + dlvl)), 5), 95)
 *
 * Both terms matter and the level term is the harsher of the two — ratings run
 * into the thousands while levels stop at 99, so out-levelling a target is worth
 * far more than out-gearing it.
 *
 * Source: Maxroll, "Diablo 2 Resurrected Hit Chance Mechanics"
 * (https://maxroll.gg/d2/resources/hit-chance-mechanics); same formula in the
 * Amazon Basin's attack/defense compendium (https://mannm.org/d2library/faqtoids/cth_eng.html).
 */
export const hitChance = (attacker, defender) => {
    const ratingTerm = attacker.attackRating / Math.max(1, attacker.attackRating + defender.defense);
    const levelTerm = attacker.level / Math.max(1, attacker.level + defender.level);
    return clamp(2 * PERCENT * ratingTerm * levelTerm, MIN_HIT_CHANCE, MAX_HIT_CHANCE);
};
/**
 * Chance for `defender` to block, in percent.
 *
 *   Block = min(floor(blocking * (dex - 15) / (clvl * 2)), 75)
 *
 * Blocking is a property of the shield; dexterity and level decide how much of
 * it the character actually realises. Floored to whole percent, never negative,
 * and zero without a shield.
 *
 * Source: Maxroll, "Chance to Block Mechanics"
 * (https://maxroll.gg/d2/resources/block-mechanics).
 */
export const blockChance = (defender) => {
    const realised = (defender.blockRating * (defender.attributes.dexterity - BLOCK_DEXTERITY_OFFSET)) /
        (Math.max(1, defender.level) * 2);
    return clamp(Math.floor(realised), 0, MAX_BLOCK_CHANCE);
};
/**
 * Item Increased Attack Speed after D2's diminishing returns:
 *
 *   EIAS = floor(120 * IAS / (120 + IAS))
 *
 * The curve is why the second 40% IAS on a build is worth so much less than the
 * first. Skill-granted attack speed skips this entirely and is linear.
 *
 * Source: Maxroll, "Attack Speed" (https://maxroll.gg/d2/resources/attack-speed).
 */
export const effectiveIncreasedAttackSpeed = (itemIas) => {
    const ias = Math.max(0, itemIas);
    return Math.floor((IAS_DIMINISHING_CONSTANT * ias) / (IAS_DIMINISHING_CONSTANT + ias));
};
/**
 * How fast this profile plays its animation, in 256ths of a source frame per
 * displayed frame.
 *
 *   pace = floor(256 * (100 + SIAS + EIAS - WSM) / 100)
 *
 * The divisor **every** frame reading of an animation goes through, which is why
 * it is stated once: `framesPerAttack` measures the whole animation with it and
 * `framesToHit` measures the moment inside it, so a profile that got faster
 * cannot get faster in one of the two and not the other.
 */
const framePace = (attack) => {
    const rate = BASE_ANIMATION_RATE +
        attack.skillAttackSpeed +
        effectiveIncreasedAttackSpeed(attack.increasedAttackSpeed) -
        attack.weaponSpeedModifier;
    return Math.max(1, Math.floor((ANIMATION_SPEED * rate) / PERCENT));
};
/**
 * How many frames one swing takes.
 *
 *   frames = ceil(animLength * 256 / floor(256 * (100 + SIAS + EIAS - WSM) / 100)) - 1
 *
 * The engine cannot show part of a frame, so speed only improves when a whole
 * frame falls off the animation: attack speed is a staircase of breakpoints, not
 * a slope. Everything between two breakpoints is wasted attack speed, which is
 * exactly the tuning decision D2 players build around.
 *
 * Source: Maxroll, "Attack Speed" (https://maxroll.gg/d2/resources/attack-speed);
 * the same equation as the Amazon Basin IAS FAQ (https://www.mannm.org/d2library/faqtoids/ias_eng.html).
 */
export const framesPerAttack = (attack) => Math.max(MIN_FRAMES_PER_ATTACK, Math.ceil((attack.animationFrames * ANIMATION_SPEED) / framePace(attack)) - 1);
/**
 * Which displayed frame the blow lands on — D2's **ActionFlag**, retimed.
 *
 * `AttackProfile.hitFrame` is an index into the *unmodified* animation, exactly
 * as `animationFrames` is a count of it, and D2 stores it that way: the
 * ActionFlag is a per-animation datum in `AnimData.D2` — *"the frame of an
 * active animation during which the assigned action — land a blow, release a
 * missile — actually happens"* — sitting beside the animation's own length.
 *
 * So the conversion is not a second formula. The 256-unit accumulator advances
 * through the ActionFlag frame at exactly the pace it advances through every
 * other frame, so both readings divide by **the same `framePace`**, which is
 * why that pace is a function of its own rather than a line inside
 * `framesPerAttack`. Two consequences fall straight out of it, and they are the
 * whole reason for choosing an index over a duration:
 *
 *   - **A faster weapon shortens the anticipation and the follow-through
 *     together.** A hit frame stated in frames-of-the-played-animation, or in
 *     ticks, would stay where it was while the animation around it collapsed —
 *     and would eventually land *after* its own swing had ended.
 *   - **A profile states one number and gets both.** The length and the moment
 *     cannot drift apart under tuning, because neither is derived from the
 *     other and both are derived from the same rate.
 *
 * Clamped into the swing at both ends. A hit frame past the animation is
 * malformed data and the closest legal answer is the last frame of it; the
 * `- 1` in `framesPerAttack` is D2's own quirk about when the *next* swing may
 * begin, and at high attack speed the two roundings can cross, which would
 * otherwise put the blow outside the animation it belongs to.
 *
 * Source: Amazon Basin, "Frames and Animations"
 * (https://www.mannm.org/d2library/faqtoids/frames_eng.html) for the ActionFlag
 * and for it being what makes an attack *"interruptable as long as the frame
 * with the ActionFlag is not played yet"* — the wind-up, in D2's own words;
 * "Increased Attack Speed" (https://www.mannm.org/d2library/faqtoids/ias_eng.html)
 * for the accumulator these both divide by.
 */
export const framesToHit = (attack) => {
    const frame = clamp(attack.hitFrame, 0, Math.max(0, attack.animationFrames - 1));
    return Math.min(Math.ceil((frame * ANIMATION_SPEED) / framePace(attack)), framesPerAttack(attack));
};
const NO_DAMAGE = createDamage();
/** Rolls a weapon's damage range. Always draws, so the roll budget stays constant. */
const rollRange = (range, rng) => rng.int(range.min, range.max + 1);
/**
 * The attribute damage bonus, in percent.
 *
 * D2 looks the weight up per weapon class and multiplies it by the attribute
 * total: strength for most melee weapons, dexterity for bows, a blend for
 * spears. Physical damage only — elemental damage on a weapon is flat.
 *
 * Source: Maxroll, "Damage Calculation" (https://maxroll.gg/d2/resources/damage-calculation).
 */
const attributeDamageBonus = (attacker) => ATTRIBUTE_NAMES.reduce((bonus, name) => bonus + attacker.attributes[name] * attacker.attack.physicalDamagePerAttribute[name], 0);
/** A swing that ended before any damage was rolled. */
const endedEarly = (chances, outcome) => ({
    ...chances,
    outcome,
    critical: false,
    damage: NO_DAMAGE,
    totalDamage: 0,
    immune: [],
});
/**
 * Resolves one swing of `attacker` against `defender`.
 *
 * Hit, block and crit are three separate rolls in that order — a crit is not a
 * better hit roll, and a block is not a failed one. Damage is then rolled per
 * type, boosted on the physical component only, and finally mitigated by the
 * defender's resistances, where a resistance of 100 or more absorbs the type
 * outright.
 */
export const resolveAttack = (attacker, defender, rng) => {
    const chances = {
        hitChance: hitChance(attacker, defender),
        blockChance: blockChance(defender),
    };
    if (!rollPercent(rng, chances.hitChance))
        return endedEarly(chances, 'miss');
    if (rollPercent(rng, chances.blockChance))
        return endedEarly(chances, 'blocked');
    const critical = rollPercent(rng, attacker.attack.criticalChance);
    const rolled = mapDamageTypes((type) => rollRange(attacker.attack.damage[type], rng));
    const boosted = Math.floor((rolled.physical * (PERCENT + attributeDamageBonus(attacker))) / PERCENT);
    const dealt = {
        ...rolled,
        physical: boosted * (critical ? CRITICAL_DAMAGE_MULTIPLIER : 1),
    };
    const { damage, immune } = applyResistances(dealt, defender.resistances, defender.resistanceCap);
    return {
        ...chances,
        outcome: 'hit',
        critical,
        damage,
        totalDamage: DAMAGE_TYPES.reduce((total, type) => total + damage[type], 0),
        immune,
    };
};
//# sourceMappingURL=combat.js.map